<template>
  <div class="trainers-manager">
    <Grid
      ref="itemsGrid"
      :add-checkmarks="true"
      :edit-action-as-link="false"
      :list-url="listUrl"
      :new-header-action-emit="true"
      :new-item-action-view="true"
      :show-header="true"
      delete-confirm-message="Delete trainer from event?"
      grid-table-class="trainers-table"
      loading-message="Loading trainers ..."
      @links-loaded="gridLinksLoaded"
      @meta-loaded="gridMetaLoaded"
      @item-action="gridItemAction"
      @header-action="onGridHeaderAction"
    />
    <Teleport
      append
      to="#vue-portal-target"
    >
      <CandidateModal
        ref="itemModal"
        :create-instructor-url="createNewUrl"
        :event="event"
        :modal-title="candidateModalTitle"
        :search-url="newItemSearchUrl"
        :show-search="candidateModalShowSearch"
        :type-id="2"
        search-new-button-title="Create new trainer"
        search-prompt-title="Find trainer"
        @success="itemModalSuccess"
      />
      <SendInvitationRemoteModal
        ref="sendInvitationRemoteModal"
        @invitation-sent="onInviteModalSent"
      />
      <CandidateDetailsModal
        ref="candidateDetailsModal"
      />
    </Teleport>
  </div>
</template>
<script lang="ts" setup>
import {nextTick, ref, useTemplateRef} from "vue";
import Grid from './grid.vue'
import CandidateModal from './candidate-modal.vue'
import SendInvitationRemoteModal from './send-invitation-remote-modal.vue'
import CandidateDetailsModal from './candidate-details-modal.vue'
import {isNonEmptyString} from "@/js/js-utils";
import type {ButtonAction, GridHeaderAction, GridItem, ItemAction} from "@/js/vue-components/types";

interface OspEvent {
  id: number;
  withTeams: boolean;
  name: string;
  number: string;
  dateRange: string;
}

defineProps({
  listUrl: {default: null, type: String}
})

const newUrl = ref<string | null>(null)
const newItemSearchUrl = ref<string | null>(null)
const createNewUrl = ref<string | null>(null)
const candidateModalTitle = ref("")
const event = ref<OspEvent | null>(null)
const candidateModalShowSearch = ref(false)

const itemModal = useTemplateRef("itemModal")
const itemsGrid = useTemplateRef("itemsGrid")
const candidateDetailsModal = useTemplateRef("candidateDetailsModal")
const sendInvitationRemoteModal = useTemplateRef("sendInvitationRemoteModal")

const showNewItemDialog = (): void => {
  candidateModalTitle.value = 'Add trainer'
  candidateModalShowSearch.value = true

  // allow modal props to update
  nextTick(() => {
    if (isNonEmptyString(newUrl.value)) {
      itemModal.value?.showModal(newUrl.value, event.value?.id || null)
    }
  })
}

const showEditItemDialog = (item: any): void => {
  const editUrl = `${item.links.edit}.json`
  candidateModalShowSearch.value = false
  candidateModalTitle.value = 'Edit trainer'

  nextTick(() => {
    itemModal.value?.showModal(editUrl, null)
  })
}
const itemModalSuccess = (): void => {
  itemModal.value?.hideModal()
  // reload candidates table view
  // this.fetchCandidates(this.fetchParams)
  itemsGrid.value?.reload()
}

const onInviteModalSent = (): void => {
  itemsGrid.value?.reload()
}

const gridLinksLoaded = (links: any): void => {
  newUrl.value = getLink(links.new)
  createNewUrl.value = getLink(links.create_new)
  newItemSearchUrl.value = getLink(links.new_search)
}

const gridMetaLoaded = (meta: any): void => {
  event.value = meta.event
}

const getLink = (value: any): string | null => {
  if (isNonEmptyString(value)) {
    return value
  }

  return null
}

const gridItemAction = (item: GridItem, action: ItemAction): void => {
  const sendInvitationActions = [
    'send-registration-invitation-details',
    'resend-registration-invitation-details',
    'send-reimbursement-invitation-details',
    'resend-reimbursement-invitation-details'
  ]


  if (action.name == 'edit') {
    showEditItemDialog(item)
  } else if (action.name === 'view-details') {
    if (isNonEmptyString(action.url)) {
      candidateDetailsModal.value?.showModal(action.url)
    }
  } else if (action.name == 'restore' && isNonEmptyString(action.url)) {
    if (confirm("Restore trainer?")) {
      itemsGrid.value?.performRequest(action.url, {}, {method: 'POST'}, (_status, _response) => {
        itemsGrid.value?.reload()
      })
    }
  } else if (sendInvitationActions.indexOf(action.name || '') != -1 && isNonEmptyString(action.url)) {
    sendInvitationRemoteModal.value?.showModal(action.url, [item.id])
  } else {
    alert(`Action not implemented: ${action.type} - ${action.name}`)
  }
}

const onGridHeaderAction = (actionOrName: string | GridHeaderAction | ButtonAction): void => {
  if (typeof actionOrName === 'string') {
    return
  }

  const action = actionOrName

  const sendInvitationActions = [
    'send-registration-invitation-details',
    'resend-registration-invitation-details',
    'send-reimbursement-invitation-details',
    'resend-reimbursement-invitation-details'
  ]

  if (action.name == 'export-registration-pdf' && isNonEmptyString(action.url)) {
    const pdfUrl = itemsGrid.value?.urlWithParams(action.url).toString()
    if (isNonEmptyString(pdfUrl)) {
      window.location.href = pdfUrl
    }
  } else if (action.name == 'add-trainer') {
    showNewItemDialog()
  } else if (sendInvitationActions.indexOf(action.name || '') != -1 && isNonEmptyString(action.url)) {
    const selectedItemIds = itemsGrid.value?.selectedItemIds || []
    const itemIds = selectedItemIds.length > 0 ? selectedItemIds : [-1]
    sendInvitationRemoteModal.value?.showModal(action.url, itemIds)
  }
}
</script>
